import { api } from '../api';

// ── Types ─────────────────────────────────────────────────────────────

export type KeycloakConfig = {
  id: string;
  org_id: string;
  server_url: string;
  realm: string;
  client_id: string;
  auto_sync_enabled: boolean;
  auto_sync_interval_minutes: number;
  last_sync_at: string | null;
  last_sync_result: SyncResult | null;
  role_mapping: Record<string, string>;
  created_at: string;
  updated_at: string;
};

export type KeycloakConfigStatus =
  | { configured: false }
  | KeycloakConfig;

export type TestConnectionResult = {
  ok: boolean;
  user_count: number;
  group_count: number;
  realm: string;
  error?: string;
};

export type KeycloakUser = {
  id: string;
  username: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  enabled: boolean;
  email_verified: boolean;
  realm_roles: string[];
  groups: string[];
  display_name: string;
  already_imported: boolean;
  suggested_role: string;
};

export type KeycloakGroup = {
  id: string;
  name: string;
  path: string;
};

export type SyncResult = {
  synced: number;
  skipped: number;
  errors: Array<{ user_id?: string; email?: string; error: string }>;
};

export type SyncStatus = {
  last_sync_at: string | null;
  last_sync_result: SyncResult | null;
  auto_sync_enabled: boolean;
};

// ── API calls ─────────────────────────────────────────────────────────

export const fetchKeycloakConfig = async (): Promise<KeycloakConfigStatus> =>
  api.get('api/admin/org/keycloak/config').json();

export const saveKeycloakConfig = async (data: {
  server_url: string;
  realm: string;
  client_id: string;
  client_secret: string;
  auto_sync_enabled?: boolean;
  auto_sync_interval_minutes?: number;
}): Promise<{ ok: boolean; config_id: string }> =>
  api.put('api/admin/org/keycloak/config', { json: data }).json();

export const testKeycloakConnection = async (): Promise<TestConnectionResult> =>
  api.post('api/admin/org/keycloak/test').json();

export const fetchKeycloakUsers = async (data?: {
  search?: string;
  group_id?: string;
  max_results?: number;
}): Promise<{ users: KeycloakUser[]; total: number }> =>
  api.post('api/admin/org/keycloak/fetch-users', { json: data ?? {} }).json();

export const fetchKeycloakGroups = async (): Promise<{ groups: KeycloakGroup[] }> =>
  api.get('api/admin/org/keycloak/groups').json();

export const executeSync = async (data?: {
  user_ids?: string[];
  role_overrides?: Record<string, string>;
}): Promise<SyncResult> =>
  api.post('api/admin/org/keycloak/sync', { json: data ?? {} }).json();

export const fetchSyncStatus = async (): Promise<SyncStatus> =>
  api.get('api/admin/org/keycloak/sync/status').json();

export const updateRoleMapping = async (data: {
  role_mapping: Record<string, string>;
}): Promise<{ ok: boolean; role_mapping: Record<string, string> }> =>
  api.put('api/admin/org/keycloak/role-mapping', { json: data }).json();
